import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import type { ReactNode } from 'react';
import { fetchProjectCreationSettings, saveProjectCreationSettings } from '@/lib/data-service';

// Field configuration types
export type ProjectFieldKey = 
  | 'projectName'
  | 'projectCode'
  | 'description'
  | 'status'
  | 'priority'
  | 'type'
  | 'startDate'
  | 'endDate'
  | 'duration'
  | 'projectManager'
  | 'projectManagers'
  | 'projectOwner'
  | 'clients'
  | 'location'
  | 'currencyCode'
  | 'budget'
  | 'tags';

export type DurationUnit = 'days' | 'weeks' | 'months' | 'years';

export interface FieldConfig {
  key: ProjectFieldKey;
  label: string;
  isRequired: boolean;
  isAlwaysRequired?: boolean; // Cannot be changed by user
  section: 'essential' | 'general' | 'timeline' | 'management';
  order: number;
  autoGenerateCode?: boolean; // For projectCode field - auto-generate from project name
}

export interface TimelineSettings {
  defaultDurationUnit: DurationUnit;
  defaultToEndDate: boolean; // true = End Date mode, false = Duration mode
  timelineCompletionRequired: boolean; // true = user must fill Start Date + (End Date OR Duration)
}

interface FieldConfigContextType {
  fieldConfigs: FieldConfig[];
  requiredFields: ProjectFieldKey[];
  timelineSettings: TimelineSettings;
  updateFieldConfig: (key: ProjectFieldKey, updates: Partial<FieldConfig>) => void;
  updateTimelineSettings: (updates: Partial<TimelineSettings>) => void;
  getRequiredFieldsBySection: (section: FieldConfig['section']) => FieldConfig[];
  getFieldConfig: (key: ProjectFieldKey) => FieldConfig | undefined;
  generateProjectCode: (projectName: string, maxLength?: number) => string;
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  isLoading: boolean;
  saveChanges: () => Promise<void>;
  resetChanges: () => void;
  resetToDefaults: () => void;
  makeAllRequired: () => void;
  makeAllOptional: () => void;
}

const FieldConfigContext = createContext<FieldConfigContextType | undefined>(undefined);

const STORAGE_KEY = 'projectFieldConfigs';
const TIMELINE_STORAGE_KEY = 'projectTimelineSettings';

// Default field configurations
const DEFAULT_FIELD_CONFIGS: FieldConfig[] = [
  // Essential Fields (Always Required)
  { key: 'projectName', label: 'Project Name', isRequired: true, isAlwaysRequired: true, section: 'essential', order: 1 },
  { key: 'projectCode', label: 'Project Code', isRequired: true, isAlwaysRequired: true, section: 'essential', order: 2 },
  
  // General Information
  { key: 'description', label: 'Description', isRequired: false, section: 'general', order: 3 },
  { key: 'status', label: 'Status', isRequired: false, section: 'general', order: 4 },
  { key: 'priority', label: 'Priority', isRequired: false, section: 'general', order: 5 },
  { key: 'type', label: 'Type', isRequired: false, section: 'general', order: 6 },
  { key: 'location', label: 'Location', isRequired: false, section: 'general', order: 7 },
  
  // Timeline & Scheduling
  { key: 'startDate', label: 'Start Date', isRequired: false, section: 'timeline', order: 8 },
  { key: 'endDate', label: 'End Date', isRequired: false, section: 'timeline', order: 9 },
  { key: 'duration', label: 'Duration', isRequired: false, section: 'timeline', order: 10 },
  
  // Management & Resources
  { key: 'projectOwner', label: 'Project Owner', isRequired: false, section: 'management', order: 11 },
  { key: 'projectManager', label: 'Project Manager (Primary)', isRequired: false, section: 'management', order: 12 },
  { key: 'projectManagers', label: 'Project Managers (Additional)', isRequired: false, section: 'management', order: 13 },
  { key: 'clients', label: 'Clients', isRequired: false, section: 'management', order: 14 },
  { key: 'currencyCode', label: 'Currency Code', isRequired: false, section: 'management', order: 15 },
  { key: 'budget', label: 'Budget', isRequired: false, section: 'management', order: 16 },
  { key: 'tags', label: 'Tags', isRequired: false, section: 'management', order: 17 },
];

// Default timeline settings
const DEFAULT_TIMELINE_SETTINGS: TimelineSettings = {
  defaultDurationUnit: 'days',
  defaultToEndDate: true, // Default to End Date mode
  timelineCompletionRequired: false, // Timeline completion is optional by default
};

export function FieldConfigProvider({ children }: { children: ReactNode }) {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const hasFetchedFromApi = useRef(false);

  // Helper function to parse field configs from localStorage or API
  const parseFieldConfigs = (saved: string | null): FieldConfig[] => {
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Migrate old isMandatory to isRequired
        return parsed.map((config: FieldConfig & { isMandatory?: boolean; isAlwaysMandatory?: boolean }) => ({
          ...config,
          isRequired: config.isRequired ?? config.isMandatory ?? false,
          isAlwaysRequired: config.isAlwaysRequired ?? config.isAlwaysMandatory ?? false,
        }));
      } catch {
        return DEFAULT_FIELD_CONFIGS;
      }
    }
    return DEFAULT_FIELD_CONFIGS;
  };

  // Helper function to parse timeline settings from localStorage or API
  const parseTimelineSettings = (saved: string | null): TimelineSettings => {
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return DEFAULT_TIMELINE_SETTINGS;
      }
    }
    return DEFAULT_TIMELINE_SETTINGS;
  };

  // Load saved configs from localStorage initially
  const [savedFieldConfigs, setSavedFieldConfigs] = useState<FieldConfig[]>(() => {
    return parseFieldConfigs(localStorage.getItem(STORAGE_KEY));
  });

  const [savedTimelineSettings, setSavedTimelineSettings] = useState<TimelineSettings>(() => {
    return parseTimelineSettings(localStorage.getItem(TIMELINE_STORAGE_KEY));
  });

  // Current working state (may have unsaved changes)
  const [fieldConfigs, setFieldConfigs] = useState<FieldConfig[]>(savedFieldConfigs);
  const [timelineSettings, setTimelineSettings] = useState<TimelineSettings>(savedTimelineSettings);

  // Sync Start Date requirement with timeline completion setting
  useEffect(() => {
    setFieldConfigs(prevConfigs => 
      prevConfigs.map(config => 
        config.key === 'startDate' 
          ? { ...config, isRequired: timelineSettings.timelineCompletionRequired }
          : config
      )
    );
  }, [timelineSettings.timelineCompletionRequired]);

  // Fetch settings from API on mount (in live mode)
  useEffect(() => {
    const loadSettingsFromApi = async () => {
      if (hasFetchedFromApi.current) return;
      hasFetchedFromApi.current = true;

      try {
        const apiSettings = await fetchProjectCreationSettings();
        if (apiSettings) {
          // API returned settings - use them and update localStorage
          const apiFieldConfigs = parseFieldConfigs(apiSettings.fieldConfigs);
          const apiTimelineSettings = parseTimelineSettings(apiSettings.timelineSettings);

          setSavedFieldConfigs(apiFieldConfigs);
          setSavedTimelineSettings(apiTimelineSettings);
          setFieldConfigs(apiFieldConfigs);
          setTimelineSettings(apiTimelineSettings);

          // Update localStorage to keep it in sync
          if (apiSettings.fieldConfigs) {
            localStorage.setItem(STORAGE_KEY, apiSettings.fieldConfigs);
          }
          if (apiSettings.timelineSettings) {
            localStorage.setItem(TIMELINE_STORAGE_KEY, apiSettings.timelineSettings);
          }
        }
        // If API returns null (demo mode or no settings), keep localStorage values
      } catch (error) {
        console.error('Failed to load project creation settings from API:', error);
        // Keep localStorage values on error
      } finally {
        setIsLoading(false);
      }
    };

    loadSettingsFromApi();
  }, []);

  // Calculate hasUnsavedChanges by comparing current state with saved state
  const hasUnsavedChanges = 
    JSON.stringify(fieldConfigs) !== JSON.stringify(savedFieldConfigs) ||
    JSON.stringify(timelineSettings) !== JSON.stringify(savedTimelineSettings);

  const requiredFields = fieldConfigs
    .filter(config => config.isRequired)
    .map(config => config.key);

  const updateFieldConfig = useCallback((key: ProjectFieldKey, updates: Partial<FieldConfig>) => {
    setFieldConfigs(prev =>
      prev.map(config => {
        if (config.key === key) {
          // Prevent changing required status for always required fields
          if (config.isAlwaysRequired && 'isRequired' in updates) {
            return config;
          }
          return { ...config, ...updates };
        }
        return config;
      })
    );
  }, []);

  const updateTimelineSettings = useCallback((updates: Partial<TimelineSettings>) => {
    setTimelineSettings(prev => {
      const newSettings = { ...prev, ...updates };
      
      // When timelineCompletionRequired changes, update Start Date requirement
      if ('timelineCompletionRequired' in updates) {
        setFieldConfigs(prevConfigs => 
          prevConfigs.map(config => 
            config.key === 'startDate' 
              ? { ...config, isRequired: newSettings.timelineCompletionRequired }
              : config
          )
        );
      }
      
      return newSettings;
    });
  }, []);

  const getRequiredFieldsBySection = useCallback((section: FieldConfig['section']) => {
    return fieldConfigs
      .filter(config => config.isRequired && config.section === section)
      .sort((a, b) => a.order - b.order);
  }, [fieldConfigs]);

  const getFieldConfig = useCallback((key: ProjectFieldKey) => {
    return fieldConfigs.find(config => config.key === key);
  }, [fieldConfigs]);

  const generateProjectCode = useCallback((projectName: string, maxLength?: number): string => {
    if (!projectName) return '';
    
    // Convert to uppercase and remove special characters
    const cleaned = projectName
      .toUpperCase()
      .replace(/[^A-Z0-9\s]/g, '')
      .trim();
    
    // Split into words and take first letter of each word (up to 3 words)
    const words = cleaned.split(/\s+/).filter(word => word.length > 0);
    const acronym = words.slice(0, 3).map(word => word[0]).join('');
    
    // Get current year
    const year = new Date().getFullYear();
    
    // Generate a simple sequential number (in real implementation, this would check for duplicates)
    const sequence = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    
    const code = `${acronym}-${year}-${sequence}`;
    
    // Respect the configured max length for project codes
    if (maxLength && code.length > maxLength) {
      return code.slice(0, maxLength);
    }
    
    return code;
  }, []);

  const saveChanges = useCallback(async () => {
    setIsSaving(true);
    try {
      const fieldConfigsJson = JSON.stringify(fieldConfigs);
      const timelineSettingsJson = JSON.stringify(timelineSettings);

      // Save to localStorage first for immediate feedback
      localStorage.setItem(STORAGE_KEY, fieldConfigsJson);
      localStorage.setItem(TIMELINE_STORAGE_KEY, timelineSettingsJson);

      // Fetch current settings to preserve fieldLengthConstraints
      const currentSettings = await fetchProjectCreationSettings();

      // Save to API (in live mode), preserving existing fieldLengthConstraints
      await saveProjectCreationSettings({
        fieldConfigs: fieldConfigsJson,
        timelineSettings: timelineSettingsJson,
        fieldLengthConstraints: currentSettings?.fieldLengthConstraints ?? null,
      });

      setSavedFieldConfigs(fieldConfigs);
      setSavedTimelineSettings(timelineSettings);
    } catch (error) {
      console.error('Failed to save project creation settings:', error);
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [fieldConfigs, timelineSettings]);

  const resetChanges = useCallback(() => {
    setFieldConfigs(savedFieldConfigs);
    setTimelineSettings(savedTimelineSettings);
  }, [savedFieldConfigs, savedTimelineSettings]);

  const resetToDefaults = useCallback(() => {
    setFieldConfigs(DEFAULT_FIELD_CONFIGS);
    setTimelineSettings(DEFAULT_TIMELINE_SETTINGS);
  }, []);

  const makeAllRequired = useCallback(() => {
    setFieldConfigs(prev =>
      prev.map(config => ({ ...config, isRequired: true }))
    );
  }, []);

  const makeAllOptional = useCallback(() => {
    setFieldConfigs(prev =>
      prev.map(config =>
        // Keep always required fields as required
        config.isAlwaysRequired ? config : { ...config, isRequired: false }
      )
    );
  }, []);

  return (
    <FieldConfigContext.Provider
      value={{
        fieldConfigs,
        requiredFields,
        timelineSettings,
        updateFieldConfig,
        updateTimelineSettings,
        getRequiredFieldsBySection,
        getFieldConfig,
        generateProjectCode,
        hasUnsavedChanges,
        isSaving,
        isLoading,
        saveChanges,
        resetChanges,
        resetToDefaults,
        makeAllRequired,
        makeAllOptional,
      }}
    >
      {children}
    </FieldConfigContext.Provider>
  );
}

export function useFieldConfig() {
  const context = useContext(FieldConfigContext);
  if (context === undefined) {
    throw new Error('useFieldConfig must be used within a FieldConfigProvider');
  }
  return context;
}
